import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { POSTS as STATIC_POSTS, POST_INDEX as STATIC_INDEX } from "@/data/blogPosts";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import Newsletter from "@/components/Newsletter";
import { Calendar, Clock, ArrowRight, User, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalise an AI-generated blog into the shape BlogPage expects.
const normaliseAiPost = (b) => ({
  slug: b.slug,
  title: b.title,
  excerpt: b.excerpt,
  category: b.category,
  read: b.read,
  image: b.image,
  author: b.author || "The To-Let Rentals Team",
  date: b.published_at ? new Date(b.published_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Recently",
  keywords: b.keywords,
  sections: b.sections || [],
  ai: true,
});

function useAllPosts() {
  const [aiPosts, setAiPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    axios.get(`${API}/blogs`).then(({ data }) => {
      const items = (data.items || []).map(normaliseAiPost);
      setAiPosts(items);
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  // AI blogs first (newest), then evergreen static ones.
  const posts = [...aiPosts, ...STATIC_POSTS];
  const index = Object.fromEntries(posts.map((p) => [p.slug, p]));
  return { posts, index, loaded };
}

function BlogIndex() {
  const { posts } = useAllPosts();
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
      <SEO
        title="Guides — Renting, Rent Agreements & Money Tips for Indian Homes"
        description="Evergreen guides from To-Let Rentals: city-by-city rental advice, rent agreement checklists, how much rent to pay, and first-time renter tips."
        path="/blog"
        keywords="rent guide india, rent agreement, first time renter, bengaluru renting, PG vs 1BHK, how much rent to pay"
      />
      <Breadcrumbs items={[{ name: "Guides", path: "/blog" }]} />
      <p className="label-eyebrow text-gray-500 mb-3">Guides & Playbooks</p>
      <h1 className="font-display text-5xl md:text-6xl text-[#7A1E2A] leading-tight mb-4">
        Rent smart. Live better.
      </h1>
      <p className="text-lg text-gray-600 max-w-2xl mb-12">
        Honest, evergreen guides from the To-Let Rentals team. No jargon, no fluff — just what to actually do before you sign your next agreement.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {posts.map((p, i) => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            data-testid={`blog-card-${p.slug}`}
            className={`group tlr-card block ${i === 0 ? "md:col-span-2" : ""}`}
          >
            <div className={`relative overflow-hidden ${i === 0 ? "h-72" : "h-56"}`}>
              <img src={p.image} alt={p.title} className="w-full h-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <span className="absolute top-4 left-4 inline-flex items-center gap-1 bg-[#C9A24A] text-[#1B0F14] text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                {p.category}
              </span>
              {p.ai && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-[#7A1E2A] text-[#FBF6EC] text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                  <Sparkles size={10} /> Fresh
                </span>
              )}
            </div>
            <div className="p-6">
              <h2 className={`font-display text-[#7A1E2A] leading-tight mb-2 ${i === 0 ? "text-3xl" : "text-2xl"}`}>
                {p.title}
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{p.excerpt}</p>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1"><Calendar size={12} /> {p.date}</span>
                <span className="inline-flex items-center gap-1"><Clock size={12} /> {p.read}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-14">
        <Newsletter source="blog-index" />
      </div>
    </div>
  );
}

function BlogPost() {
  const { slug } = useParams();
  const { index, posts, loaded } = useAllPosts();
  // Static index available immediately, AI blogs come after fetch — combine safely
  const post = index[slug] || STATIC_INDEX[slug];
  if (!post) {
    if (!loaded) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-gray-500">Loading…</div>;
    return <Navigate to="/blog" replace />;
  }

  const related = posts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
        keywords={post.keywords}
        breadcrumbs={[
          { name: "Guides", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.image,
          "author": { "@type": "Organization", "name": post.author },
          "publisher": { "@type": "Organization", "name": "To-Let Rentals" },
          "datePublished": post.date,
        }}
      />
      <Breadcrumbs items={[
        { name: "Guides", path: "/blog" },
        { name: post.title, path: `/blog/${post.slug}` },
      ]} />

      <div className="flex items-center gap-2 mb-4">
        <span className="inline-block bg-[#C9A24A] text-[#1B0F14] text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
          {post.category}
        </span>
        {post.ai && (
          <span className="inline-flex items-center gap-1 bg-[#7A1E2A] text-[#FBF6EC] text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
            <Sparkles size={10} /> Fresh
          </span>
        )}
      </div>
      <h1 className="font-display text-4xl md:text-5xl text-[#7A1E2A] leading-tight mb-4" data-testid="blog-post-title">
        {post.title}
      </h1>
      <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
        <span className="inline-flex items-center gap-1"><User size={14} /> {post.author}</span>
        <span className="inline-flex items-center gap-1"><Calendar size={14} /> {post.date}</span>
        <span className="inline-flex items-center gap-1"><Clock size={14} /> {post.read}</span>
      </div>

      <div className="relative h-72 md:h-96 rounded-2xl overflow-hidden mb-10">
        <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
      </div>

      <p className="text-xl text-gray-800 leading-relaxed mb-10 font-display italic">{post.excerpt}</p>

      <article className="space-y-8">
        {post.sections.map((s, i) => (
          <section key={`${s.h}-${i}`}>
            <h2 className="font-display text-2xl md:text-3xl text-[#7A1E2A] mb-3">{s.h}</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{s.p}</p>
          </section>
        ))}
      </article>

      {/* Newsletter capture — inline after body */}
      <div className="mt-12">
        <Newsletter source={`blog:${post.slug}`} />
      </div>

      {/* CTA */}
      <div className="mt-14 p-8 rounded-2xl bg-gradient-to-br from-[#7A1E2A] to-[#5C1420] text-white">
        <h3 className="font-display text-2xl mb-2">Ready to find your next home?</h3>
        <p className="text-white/85 mb-5">Zero brokerage. Verified owners. Free forever.</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/properties" className="tlr-gold-btn rounded-full px-6 py-3 inline-flex items-center gap-2">Browse properties <ArrowRight size={14} /></Link>
          <Link to="/discover" className="rounded-full px-6 py-3 border border-white/30 text-white hover:bg-white/10">Scroll walkthroughs</Link>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-14">
          <p className="label-eyebrow text-gray-500 mb-4">Related guides</p>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="tlr-card block group">
                <div className="h-32 overflow-hidden">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover img-zoom" />
                </div>
                <div className="p-4">
                  <p className="text-[10px] tracking-widest uppercase text-[#C9A24A] font-bold mb-1">{r.category}</p>
                  <h4 className="font-display text-lg text-[#7A1E2A] leading-tight">{r.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function BlogPage() {
  const { slug } = useParams();
  return slug ? <BlogPost /> : <BlogIndex />;
}
